import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useLedgerPostings, useRetryLedgerPosting } from 'hooks/api/useLedgerPostings';
import { LEDGER_POSTING_STATUSES } from 'api/types';
import type { LedgerPosting, LedgerPostingSearchRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import PostingViewModal from 'components/modals/modules/ledger-mapping-module/PostingViewModal';

const statusFilterOptions: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  ...LEDGER_POSTING_STATUSES.map(s => ({ value: s, label: s.replace('_', ' ') })),
];

const sourceTypeFilterOptions: SelectOption[] = [
  { value: '', label: 'All Sources' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'PAYMENT_VOID', label: 'Payment Void' },
];

const STATUS_BADGE: Record<string, string> = {
  POSTED: 'badge-active',
  PENDING: 'badge-pending',
  PUBLISHED: 'badge-pending',
  FAILED: 'badge-error',
  MANUAL_REVIEW: 'badge-error',
  SKIPPED: 'badge-inactive',
};

interface PostingRegisterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** Fail-closed RBAC actions from admin.tbl_role_permissions. */
  allowedActions?: string[];
}

/**
 * The operator's window onto Fairbooks posting: what posted, what is stuck and
 * why. MANUAL_REVIEW is the queue that needs a human — fix the mapping, then
 * Retry (the payload is rebuilt from current configuration).
 */
const PostingRegisterScreen: React.FC<PostingRegisterScreenProps> = ({ moduleIcon, allowedActions = [] }) => {
  const [queryParams, setQueryParams] = useState<LedgerPostingSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useLedgerPostings(queryParams);
  const { retryLedgerPosting, loading: isRetrying } = useRetryLedgerPosting();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSourceType, setFilterSourceType] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState<LedgerPosting | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleView = useCallback((item: LedgerPosting) => {
    setSelectedPosting(item);
    setViewModalOpen(true);
  }, []);

  const handleRetryClick = useCallback((item: LedgerPosting) => {
    setSelectedPosting(item);
    setViewModalOpen(false);
    setRetryConfirmOpen(true);
  }, []);

  const handleConfirmRetry = useCallback(async () => {
    if (!selectedPosting) return;
    try {
      const result = await retryLedgerPosting(selectedPosting.id);
      setRetryConfirmOpen(false);
      if (result?.status === 'POSTED') {
        notificationService.toast.success(`Posting ${selectedPosting.idempotencyKey || ''} is posted to Fairbooks.`);
      } else {
        notificationService.toast.info(`Posting retried — current status: ${result?.status ?? 'unknown'}.`);
      }
      setSelectedPosting(null);
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      notificationService.toast.error(msg || 'Retry failed. Please try again.');
    }
  }, [selectedPosting, retryLedgerPosting, refetch]);

  const columns: SyutGridColumn<LedgerPosting>[] = useMemo(() => [
    {
      id: 'createdAt', header: 'Created', accessorKey: 'createdAt', size: 150, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        return <span style={{ fontSize: '0.82rem' }}>{v ? new Date(v).toLocaleString() : '-'}</span>;
      },
    },
    {
      id: 'idempotencyKey', header: 'Reference', accessorKey: 'idempotencyKey', size: 220, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'sourceType', header: 'Source', accessorKey: 'sourceType', size: 120,
      cell: ({ getValue }) => {
        const v = getValue() as string;
        return <span>{v === 'PAYMENT' ? 'Payment' : v === 'PAYMENT_VOID' ? 'Payment Void' : v}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 140, enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue() as string;
        return <span className={`badge ${STATUS_BADGE[v] || 'badge-pending'}`}>{v.replace('_', ' ')}</span>;
      },
    },
    { id: 'attemptCount', header: 'Attempts', accessorKey: 'attemptCount', size: 90 },
    {
      id: 'externalReference', header: 'Fairbooks Ref', accessorKey: 'externalReference', size: 150,
      cell: ({ getValue }) => <span style={{ fontSize: '0.82rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'lastError', header: 'Last Error', accessorKey: 'lastError', size: 300,
      cell: ({ getValue }) => {
        const v = (getValue() as string) || '';
        return (
          <span style={{ fontSize: '0.8rem', color: v ? 'var(--danger, #dc2626)' : 'var(--text-secondary)' }} title={v}>
            {v ? (v.length > 80 ? `${v.slice(0, 80)}…` : v) : '-'}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((item: LedgerPosting): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(item) });
    }
    if (allowedActions.includes('retry') && item.status !== 'POSTED') {
      items.push({ id: 'retry', label: 'Retry', icon: <IconSystem.refresh size={18} />, onClick: () => handleRetryClick(item) });
    }
    return items;
  }, [allowedActions, handleView, handleRetryClick]);

  const handleExport = useCallback(() => {
    if (data.length === 0) {
      notificationService.toast.warning('No data to export');
      return;
    }
    const headers = ['Created', 'Reference', 'Source', 'Status', 'Attempts', 'Fairbooks Ref', 'Last Error'];
    const rows = data.map(p => [
      p.createdAt ? new Date(p.createdAt).toLocaleString() : '',
      p.idempotencyKey || '', p.sourceType, p.status, String(p.attemptCount),
      p.externalReference || '', p.lastError || '',
    ]);
    const csvContent = [headers, ...rows]
      .map(r => r.map(v => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `posting_register_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [data]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    }
    return buttons;
  }, [allowedActions, refetch, handleExport]);

  const appliedFilterCount = [filterStatus, filterSourceType, filterFrom, filterTo].filter(Boolean).length;

  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      status: filterStatus || undefined,
      sourceType: filterSourceType || undefined,
      createdFrom: filterFrom || undefined,
      createdTo: filterTo || undefined,
      page: 1,
    }));
  }, [filterStatus, filterSourceType, filterFrom, filterTo]);

  const handleFilterClear = useCallback(() => {
    setFilterStatus('');
    setFilterSourceType('');
    setFilterFrom('');
    setFilterTo('');
    setQueryParams(prev => ({
      ...prev, status: undefined, sourceType: undefined, createdFrom: undefined, createdTo: undefined, page: 1,
    }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Fairbooks Posting Register"
        description="Every advance handed to Fairbooks — what posted, what is stuck and why"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search by reference...' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: appliedFilterCount }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage="No postings yet — rows appear here when advances are recorded with Fairbooks posting enabled."
              frozenRightColumns={['actions']}
            />
          </div>

          {filterOpen && (
            <div className="search-panel">
              <div className="search-panel-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <IconSystem.filter size={14} />
                  <h3 className="search-panel-title">Filters</h3>
                </div>
                <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }} title="Close filters">
                  <IconSystem.x size={16} />
                </button>
              </div>
              <div className="search-panel-content">
                <div className="filter-section">
                  <SyutSelect
                    label="Status"
                    options={statusFilterOptions}
                    value={filterStatus}
                    onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                    isClearable={false}
                    size="sm"
                  />
                </div>
                <div className="filter-section">
                  <SyutSelect
                    label="Source"
                    options={sourceTypeFilterOptions}
                    value={filterSourceType}
                    onChange={(val: string | string[] | null) => setFilterSourceType((val as string) || '')}
                    isClearable={false}
                    size="sm"
                  />
                </div>
                <div className="filter-section">
                  <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created From</label>
                  <input type="date" className="form-input" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
                <div className="filter-section">
                  <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created To</label>
                  <input type="date" className="form-input" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-actions">
                <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
                <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PostingViewModal
        isOpen={viewModalOpen}
        posting={selectedPosting}
        onClose={() => setViewModalOpen(false)}
        onRetry={handleRetryClick}
        canRetry={allowedActions.includes('retry')}
      />

      <ConfirmDialog
        open={retryConfirmOpen}
        title="Retry Posting"
        message={`Re-send "${selectedPosting?.idempotencyKey || ''}" to Fairbooks? The payload is rebuilt from current mapping configuration, and Fairbooks' reference dedupe prevents a duplicate receipt.`}
        confirmLabel="Retry"
        cancelLabel="Cancel"
        variant="default"
        loading={isRetrying}
        loadingMessage="Retrying..."
        onConfirm={handleConfirmRetry}
        onCancel={() => setRetryConfirmOpen(false)}
      />
    </div>
  );
};

export default PostingRegisterScreen;
